import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col, Input, InputNumber,
    Typography, Avatar, Spin, Empty, Statistic, message, Modal, Popover, Select, Divider, DatePicker, Dropdown, Segmented
} from 'antd';
import {
    ArrowLeftOutlined, ReloadOutlined,
    CheckCircleOutlined, ClockCircleOutlined,
    CarOutlined, PhoneOutlined, TrophyOutlined,
    WalletOutlined, TeamOutlined, CrownOutlined,
    SearchOutlined, EyeOutlined, EditOutlined,
    DollarOutlined, CopyOutlined, InfoCircleOutlined,
    CalendarOutlined, FieldTimeOutlined, ExclamationCircleOutlined,
    DownloadOutlined, FileExcelOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Configuration des paliers avec seuils et montants dynamiques par ville
const getAffiliateMilestones = (t = {}, r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'account_creation', milestoneKey: 'accountCreation', rides: 0, amount: aff.accountCreation || 500, label: 'Compte' },
        { key: 'activation_rides_5', milestoneKey: 'rides5', rides: t.milestoneThreshold1 || 5, amount: aff.rides5 || 900, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', milestoneKey: 'rides15', rides: t.milestoneThreshold2 || 15, amount: aff.rides15 || 1350, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', milestoneKey: 'rides35', rides: t.milestoneThreshold3 || 35, amount: aff.rides35 || 1800, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', milestoneKey: 'rides55', rides: t.milestoneThreshold4 || 55, amount: aff.rides55 || 1800, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const getAffiliateReactivationMilestones = (r = {}) => {
    const aff = r.affiliate || {};
    return [
        { key: 'account_creation', milestoneKey: 'accountCreation', rides: 0, amount: aff.accountCreation || 500, label: 'Compte' },
        { key: 'reactivation_rides_20', milestoneKey: 'rides20Reactivation', rides: 20, amount: aff.reactivationRides20 || 1500, label: '20 courses' },
        { key: 'reactivation_hours_130', milestoneKey: 'hours130Reactivation', rides: 0, hours: 130, amount: aff.reactivationHours130 || 900, label: '130 heures' },
    ];
};

const getAmbassadorMilestones = (t = {}, r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', milestoneKey: 'accountCreation', rides: 0, amount: amb.accountCreation || 50, label: 'Compte' },
        { key: 'activation_rides_5', milestoneKey: 'rides5', rides: t.milestoneThreshold1 || 5, amount: amb.rides5 || 100, label: `${t.milestoneThreshold1 || 5} courses` },
        { key: 'activation_rides_15', milestoneKey: 'rides15', rides: t.milestoneThreshold2 || 15, amount: amb.rides15 || 150, label: `${t.milestoneThreshold2 || 15} courses` },
        { key: 'activation_rides_35', milestoneKey: 'rides35', rides: t.milestoneThreshold3 || 35, amount: amb.rides35 || 200, label: `${t.milestoneThreshold3 || 35} courses` },
        { key: 'activation_rides_55', milestoneKey: 'rides55', rides: t.milestoneThreshold4 || 55, amount: amb.rides55 || 200, label: `${t.milestoneThreshold4 || 55} courses` },
    ];
};

const getAmbassadorReactivationMilestones = (r = {}) => {
    const amb = r.ambassador || {};
    return [
        { key: 'account_creation', milestoneKey: 'accountCreation', rides: 0, amount: amb.accountCreation || 50, label: 'Compte' },
        { key: 'reactivation_rides_20', milestoneKey: 'rides20Reactivation', rides: 20, amount: amb.reactivationRides20 || 200, label: '20 courses' },
        { key: 'reactivation_hours_130', milestoneKey: 'hours130Reactivation', rides: 0, hours: 130, amount: amb.reactivationHours130 || 100, label: '130 heures' },
    ];
};

// Fonction pour obtenir les milestones selon le type
const getMilestonesForDriver = (isAffiliate, driverType, thresholds = {}, rates = {}) => {
    if (driverType === 'reactivation') {
        return isAffiliate ? getAffiliateReactivationMilestones(rates) : getAmbassadorReactivationMilestones(rates);
    }
    return isAffiliate ? getAffiliateMilestones(thresholds, rates) : getAmbassadorMilestones(thresholds, rates);
};

const RevenuesDetailPage = () => {
    const { type, id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [beneficiary, setBeneficiary] = useState(null);
    const [drivers, setDrivers] = useState([]);
    const [milestoneThresholds, setMilestoneThresholds] = useState({});
    const [commissionRates, setCommissionRates] = useState({});
    const [searchText, setSearchText] = useState('');
    const [paymentModal, setPaymentModal] = useState({ visible: false, driver: null });
    const [paymentRef, setPaymentRef] = useState('');
    const [detailModal, setDetailModal] = useState({ visible: false, driver: null });
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null); // null = tous, 'active' = actifs, 'completed' = complétés, 'expired' = expirés
    const [cityFilter, setCityFilter] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [stats, setStats] = useState({
        totalRevenue: 0,
        paidAmount: 0,
        pendingAmount: 0,
        approvedAmount: 0,
        commissionsCount: 0
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.getRevenueDetails(type, id);
            if (response.success) {
                setBeneficiary(response.beneficiary || {});
                if (Array.isArray(response.drivers)) {
                    setDrivers(response.drivers);
                } else if (response.drivers) {
                    const allDrivers = [
                        ...(response.drivers.direct || []).map(d => ({ ...d, isDirect: true })),
                        ...(response.drivers.indirect || []).map(d => ({ ...d, isDirect: false }))
                    ];
                    setDrivers(allDrivers);
                }
                setStats(response.stats || {});
                if (response.milestoneThresholds) {
                    setMilestoneThresholds(response.milestoneThresholds);
                }
                if (response.commissionRates) {
                    setCommissionRates(response.commissionRates);
                }
            } else {
                message.error(response.message || 'Erreur lors du chargement');
            }
        } catch (error) {
            console.error('Erreur chargement détails revenus:', error);
            message.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [type, id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const isAffiliate = type === 'affiliate';
    const themeColor = isAffiliate ? '#1890ff' : '#722ed1';

    const totalDrivers = drivers.length;
    // Pour nouveau chauffeur: 5 paliers, pour réactivation: 3 paliers
    const completedDrivers = drivers.filter(d => {
        const targetMilestones = d.driverType === 'reactivation' ? 3 : 5;
        return (d.milestonesCount || 0) >= targetMilestones;
    }).length;
    const totalRevenue = stats.totalRevenue || 0;

    // Calculer les montants payés depuis les chauffeurs (plus précis que les commissions)
    const paidFullDrivers = drivers.filter(d => d.paymentStatus === 'paid');
    const paidPartialDrivers = drivers.filter(d => d.paymentStatus === 'partial');

    // Montant payé complet = somme des gains des chauffeurs avec statut 'paid'
    const paidFullAmount = paidFullDrivers.reduce((sum, d) => sum + (d.totalRevenue || d.commissionsGenerated || 0), 0);

    // Montant payé partiellement = somme des paidAmount des chauffeurs avec statut 'partial'
    const paidPartialAmount = paidPartialDrivers.reduce((sum, d) => sum + (d.paidAmount || 0), 0);

    // Total payé
    const paidAmount = paidFullAmount + paidPartialAmount;

    // Reste à payer sur les paiements partiels
    const remainingOnPartial = paidPartialDrivers.reduce((sum, d) => {
        const gains = d.totalRevenue || d.commissionsGenerated || 0;
        const paid = d.paidAmount || 0;
        return sum + Math.max(0, gains - paid);
    }, 0);

    // Montant réel à payer = total revenus - tout ce qui a été payé (complet + partiel)
    const actualAmountToPay = totalRevenue - paidFullAmount - paidPartialAmount;

    // Calculer le statut de la fenêtre d'activation
    const getWindowStatus = (record) => {
        if (!record.activationWindowEnd) return { status: 'unknown', text: '-', color: '#bfbfbf' };

        const now = dayjs();
        const endDate = dayjs(record.activationWindowEnd);
        const diffDays = endDate.diff(now, 'day');
        const diffHours = endDate.diff(now, 'hour') % 24;

        // Vérifier si tous les objectifs sont atteints
        // Pour réactivation: 3 paliers (compte, 20 courses, 130h)
        // Pour nouveau: 5 paliers (compte, 5, 15, 35, 55)
        const isReactivation = record.driverType === 'reactivation';
        const milestonesCount = record.milestonesCount || 0;
        const targetMilestones = isReactivation ? 3 : 5;
        const isCompleted = milestonesCount >= targetMilestones;

        if (isCompleted) {
            return { status: 'completed', text: 'Complété', color: '#52c41a', icon: <CheckCircleOutlined /> };
        }

        if (now.isAfter(endDate)) {
            return { status: 'expired', text: 'Expiré', color: '#ff4d4f', icon: <ExclamationCircleOutlined /> };
        }

        if (diffDays === 0) {
            return { status: 'urgent', text: `${diffHours}h`, color: '#faad14', icon: <ClockCircleOutlined /> };
        }

        return { status: 'active', text: `${diffDays}j ${diffHours}h`, color: '#1890ff', icon: <FieldTimeOutlined /> };
    };

    const renderMilestoneTag = (achieved, label) => (
        <Tag
            style={{
                margin: '1px',
                padding: '0 6px',
                borderRadius: '10px',
                fontSize: 11,
                border: achieved ? '1px solid #52c41a' : '1px solid #d9d9d9',
                background: achieved ? '#f6ffed' : '#fafafa',
                color: achieved ? '#52c41a' : '#999'
            }}
            icon={achieved ? <CheckCircleOutlined style={{ fontSize: 10 }} /> : null}
        >
            {label}
        </Tag>
    );

    // Extraire les villes uniques des chauffeurs
    const cityOptions = [...new Map(
        drivers
            .filter(d => d.city?.name)
            .map(d => [d.city.name, { label: d.city.name, value: d.city.name }])
    ).values()].sort((a, b) => a.label.localeCompare(b.label));

    const filterDrivers = (driversList) => {
        let filtered = driversList;

        // Filtrer par ville
        if (cityFilter) {
            filtered = filtered.filter(d => d.city?.name === cityFilter);
        }

        // Filtrer par recherche texte
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(d =>
                (d.firstName?.toLowerCase() || '').includes(search) ||
                (d.lastName?.toLowerCase() || '').includes(search) ||
                (d.phone || '').includes(search)
            );
        }

        // Filtrer par période
        if (dateRange && dateRange[0] && dateRange[1]) {
            const startDate = dateRange[0].startOf('day');
            const endDate = dateRange[1].endOf('day');
            filtered = filtered.filter(d => {
                const recruitDate = dayjs(d.trackingStartDate || d.recruitedAt);
                return recruitDate.isAfter(startDate) && recruitDate.isBefore(endDate);
            });
        }

        // Filtrer par statut
        if (statusFilter) {
            filtered = filtered.filter(d => {
                const status = getWindowStatus(d).status;
                if (statusFilter === 'active') {
                    // Actifs = ceux qui ne sont ni expirés ni complétés (urgent, active)
                    return status === 'active' || status === 'urgent';
                } else if (statusFilter === 'completed') {
                    return status === 'completed';
                } else if (statusFilter === 'expired') {
                    return status === 'expired';
                }
                return true;
            });
        }

        // Trier: Actifs > Complétés > Expirés, puis par nombre de courses décroissant
        // Pour les expirés: sous-tri par statut de paiement (attente > partiel > payé)
        filtered = [...filtered].sort((a, b) => {
            const statusA = getWindowStatus(a).status;
            const statusB = getWindowStatus(b).status;

            // Définir l'ordre de priorité: actif/urgent (1) > completed (2) > expired (3)
            const getPriority = (status) => {
                if (status === 'active' || status === 'urgent') return 1;
                if (status === 'completed') return 2;
                if (status === 'expired') return 3;
                return 4; // unknown
            };

            const priorityA = getPriority(statusA);
            const priorityB = getPriority(statusB);

            // Si les priorités sont différentes, trier par priorité
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // Pour les expirés, sous-trier par statut de paiement: attente (1) > partiel (2) > payé (3)
            if (statusA === 'expired' && statusB === 'expired') {
                const getPaymentPriority = (d) => {
                    const ps = d.paymentStatus || 'pending';
                    if (ps === 'pending') return 1;
                    if (ps === 'partial') return 2;
                    if (ps === 'paid') return 3;
                    return 1;
                };
                const payPriorityA = getPaymentPriority(a);
                const payPriorityB = getPaymentPriority(b);
                if (payPriorityA !== payPriorityB) {
                    return payPriorityA - payPriorityB;
                }
            }

            // Sinon, trier par date la plus récente en premier
            const dateA = new Date(a.trackingStartDate || a.recruitedAt || 0).getTime();
            const dateB = new Date(b.trackingStartDate || b.recruitedAt || 0).getTime();
            return dateB - dateA;
        });

        return filtered;
    };

    // Fonction pour préparer les données d'export
    const prepareExportData = () => {
        const dataToExport = filterDrivers(drivers);
        return dataToExport.map(d => {
            const currentGains = d.totalRevenue || d.commissionsGenerated || 0;
            const alreadyPaid = d.paidAmount || 0;
            const remaining = Math.max(0, currentGains - alreadyPaid);
            const windowStatus = getWindowStatus(d);

            return {
                'Nom': `${d.firstName || ''} ${d.lastName || ''}`.trim(),
                'Téléphone': d.phone || '',
                'Courses': d.currentStats?.totalRides || d.totalRides || 0,
                'Type': d.driverType === 'reactivation' ? 'Réactivation' : 'Nouveau',
                'Date recrutement': d.trackingStartDate || d.recruitedAt ? dayjs(d.trackingStartDate || d.recruitedAt).format('DD/MM/YYYY') : '',
                'Fin période': d.activationWindowEnd ? dayjs(d.activationWindowEnd).format('DD/MM/YYYY') : '',
                'Statut période': windowStatus.text,
                'Gains (F)': currentGains,
                'Payé (F)': alreadyPaid,
                'Reste (F)': remaining,
                'Statut paiement': d.paymentStatus === 'paid' ? 'Payé' : d.paymentStatus === 'partial' ? 'Partiel' : 'En attente',
                'Référence': d.paymentReference || ''
            };
        });
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${beneficiary?.firstName || ''} ${beneficiary?.lastName || ''}`.trim();
            const benefType = isAffiliate ? 'Affilié' : 'Ambassadeur';

            // Construire le titre et la période
            const titleLine = `Liste des revenus de ${benefName} (${benefType})`;
            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `Période: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodLine = `Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(data[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '', // Ligne vide
                headers.join(';'),
                ...data.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `revenus_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel (XLS)
    const exportToExcel = () => {
        setExporting(true);
        try {
            const data = prepareExportData();
            if (data.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const benefName = `${beneficiary?.firstName || ''} ${beneficiary?.lastName || ''}`.trim();
            const benefType = isAffiliate ? 'AFFILIÉ' : 'AMBASSADEUR';

            // Styles
            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "1890FF" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            // Créer les lignes d'en-tête (en MAJUSCULES)
            const titleText = `LISTE DES REVENUS DE ${benefName.toUpperCase()} (${benefType})`;
            const periodText = dateRange && dateRange[0] && dateRange[1]
                ? `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`
                : `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm').toUpperCase()}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                [] // Ligne vide
            ];

            // Créer le worksheet avec les en-têtes d'abord
            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            // Préparer les données avec les en-têtes de colonnes
            const headers = Object.keys(data[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...data.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            // Ajouter les données à partir de la ligne 4 (index 3)
            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Chauffeurs');

            // Ajuster la largeur des colonnes
            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...data.map(row => String(row[key] || '').length), 15) + 2
            }));
            worksheet['!cols'] = colWidths;

            // Fusionner les cellules pour le titre (première ligne)
            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }, // Titre
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }  // Période
            ];

            // Hauteur des lignes
            worksheet['!rows'] = [
                { hpt: 25 }, // Titre
                { hpt: 20 }, // Période
                { hpt: 15 }, // Ligne vide
                { hpt: 22 }  // En-têtes
            ];

            const benefNameFile = benefName.replace(/\s+/g, '_');
            const filename = `revenus_${benefNameFile}_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    // Menu d'export
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const [paymentStatusValue, setPaymentStatusValue] = useState('pending');
    const [paymentAmountValue, setPaymentAmountValue] = useState(0);
    const [paymentMode, setPaymentMode] = useState('wave');

    // Calculer le maximum possible selon le type de chauffeur
    const getMaxPossibleGains = (driver) => {
        const isReactivation = driver.driverType === 'reactivation';
        if (isAffiliate) {
            // Affilié: nouveau = 6350F, réactivation = 2900F
            return isReactivation ? 2900 : 6350;
        } else {
            // Ambassadeur: nouveau = 700F, réactivation = 350F
            return isReactivation ? 350 : 700;
        }
    };

    const handlePaymentClick = (driver) => {
        setPaymentModal({ visible: true, driver });
        setPaymentRef(driver.paymentReference || '');
        setPaymentStatusValue(driver.paymentStatus || 'pending');
        setPaymentMode('wave');
        // Pour un nouveau paiement partiel, proposer les gains actuels moins ce qui est déjà payé
        const currentGains = driver.totalRevenue || driver.commissionsGenerated || 0;
        const alreadyPaid = driver.paidAmount || 0;
        const remainingToPay = Math.max(0, currentGains - alreadyPaid);
        setPaymentAmountValue(remainingToPay);
    };

    const [savingPayment, setSavingPayment] = useState(false);
    const [sendingWavePayout, setSendingWavePayout] = useState(false);

    // Paiement via Wave Payout API
    const handleWavePayout = async () => {
        if (!paymentModal.driver?.progressId) {
            message.error('Impossible de payer: progression non trouvée');
            return;
        }

        const currentGains = paymentModal.driver.totalRevenue || paymentModal.driver.commissionsGenerated || 0;
        const alreadyPaid = paymentModal.driver.paidAmount || 0;
        const amountToPay = paymentStatusValue === 'partial' ? paymentAmountValue : Math.max(0, currentGains - alreadyPaid);

        if (amountToPay <= 0) {
            message.error('Le montant à payer doit être supérieur à 0');
            return;
        }

        // Confirmation avant envoi
        Modal.confirm({
            title: 'Confirmer le paiement Wave',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Vous allez envoyer <strong>{amountToPay.toLocaleString()} F</strong> via Wave à :</p>
                    <p><strong>{beneficiary?.firstName} {beneficiary?.lastName}</strong></p>
                    <p style={{ color: '#8c8c8c' }}>{beneficiary?.phone}</p>
                    <p style={{ marginTop: 12, color: '#faad14' }}>
                        Cette action est irréversible. L'argent sera débité de votre compte Wave Business.
                    </p>
                </div>
            ),
            okText: 'Confirmer et payer',
            cancelText: 'Annuler',
            okButtonProps: { style: { background: '#1890ff' } },
            onOk: async () => {
                setSendingWavePayout(true);
                try {
                    const response = await affiliatesService.sendWavePayout({
                        progressId: paymentModal.driver.progressId,
                        recipientType: isAffiliate ? 'affiliate' : 'ambassador',
                        amount: amountToPay,
                        paymentReason: `Commission ${isAffiliate ? 'affilié' : 'ambassadeur'}`
                    });

                    if (response.success) {
                        message.success(`Paiement Wave effectué avec succès ! Référence: ${response.data.transactionId}`);
                        setPaymentRef(response.data.transactionId);
                        setPaymentModal({ visible: false, driver: null });
                        setPaymentRef('');
                        setPaymentStatusValue('pending');
                        setPaymentAmountValue(0);
                        fetchData();
                    } else {
                        message.error(response.message || 'Erreur lors du paiement Wave');
                    }
                } catch (error) {
                    console.error('Erreur Wave Payout:', error);
                    message.error(error.response?.data?.message || 'Erreur lors du paiement Wave');
                } finally {
                    setSendingWavePayout(false);
                }
            }
        });
    };

    const handleSavePayment = async () => {
        if (!paymentModal.driver?.progressId) {
            message.error('Impossible de sauvegarder: progression non trouvée');
            return;
        }

        // Validation du montant pour paiement partiel
        if (paymentStatusValue === 'partial' && (!paymentAmountValue || paymentAmountValue <= 0)) {
            message.error('Veuillez entrer un montant valide pour le paiement partiel');
            return;
        }

        setSavingPayment(true);
        try {
            const currentGains = paymentModal.driver.totalRevenue || paymentModal.driver.commissionsGenerated || 0;
            const alreadyPaid = paymentModal.driver.paidAmount || 0;
            // Le maximum possible selon le type de chauffeur (6350F pour nouveau affilié, etc.)
            const maxPossible = getMaxPossibleGains(paymentModal.driver);

            const response = await affiliatesService.updateDriverPayment(paymentModal.driver.progressId, {
                paymentReference: paymentRef,
                paymentStatus: paymentStatusValue,
                paymentAmount: paymentStatusValue === 'partial' ? paymentAmountValue : (paymentStatusValue === 'paid' ? currentGains - alreadyPaid : 0),
                // Envoyer le maximum possible pour déterminer si c'est vraiment complet
                totalGains: maxPossible,
                recipientType: isAffiliate ? 'affiliate' : 'ambassador'
            });

            if (response.success) {
                message.success('Paiement mis à jour avec succès');
                setPaymentModal({ visible: false, driver: null });
                setPaymentRef('');
                setPaymentStatusValue('pending');
                setPaymentAmountValue(0);
                fetchData();
            } else {
                message.error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur sauvegarde paiement:', error);
            message.error('Erreur lors de la sauvegarde du paiement');
        } finally {
            setSavingPayment(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success('Copié !');
    };

    // Barème popover content - affiche les deux types
    const newDriverMilestones = isAffiliate ? getAffiliateMilestones(milestoneThresholds, commissionRates) : getAmbassadorMilestones(milestoneThresholds, commissionRates);
    const reactivationMilestones = isAffiliate ? getAffiliateReactivationMilestones(commissionRates) : getAmbassadorReactivationMilestones(commissionRates);
    const newDriverTotal = newDriverMilestones.reduce((sum, m) => sum + m.amount, 0);
    const reactivationTotal = reactivationMilestones.reduce((sum, m) => sum + m.amount, 0);

    const baremeContent = (
        <div style={{ width: 320 }}>
            {/* Barème Nouveau Chauffeur */}
            <div style={{ marginBottom: 16 }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: '#1890ff',
                    borderBottom: '2px solid #1890ff',
                    paddingBottom: 4
                }}>
                    <CarOutlined style={{ marginRight: 6 }} />
                    Nouveau Chauffeur
                </div>
                {newDriverMilestones.map((m, i) => (
                    <div key={m.key} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        borderBottom: i < newDriverMilestones.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                        <span style={{ color: '#595959' }}>{m.label}</span>
                        <span style={{ fontWeight: 600, color: '#52c41a' }}>+{m.amount} F</span>
                    </div>
                ))}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0 0',
                    marginTop: 4,
                    borderTop: '1px solid #d9d9d9',
                    fontWeight: 600
                }}>
                    <span>Total</span>
                    <span style={{ color: '#1890ff' }}>{newDriverTotal} F</span>
                </div>
            </div>

            {/* Barème Réactivation */}
            <div>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: '#fa8c16',
                    borderBottom: '2px solid #fa8c16',
                    paddingBottom: 4
                }}>
                    <ReloadOutlined style={{ marginRight: 6 }} />
                    Réactivation
                </div>
                {reactivationMilestones.map((m, i) => (
                    <div key={m.key} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        borderBottom: i < reactivationMilestones.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                        <span style={{ color: '#595959' }}>{m.label}</span>
                        <span style={{ fontWeight: 600, color: '#52c41a' }}>+{m.amount} F</span>
                    </div>
                ))}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0 0',
                    marginTop: 4,
                    borderTop: '1px solid #d9d9d9',
                    fontWeight: 600
                }}>
                    <span>Total</span>
                    <span style={{ color: '#fa8c16' }}>{reactivationTotal} F</span>
                </div>
            </div>
        </div>
    );

    const driverColumns = [
        {
            title: 'Chauffeur',
            key: 'name',
            width: 220,
            fixed: 'left',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar
                        size={36}
                        style={{
                            background: record.isDirect ? '#f9f0ff' : '#e6f7ff',
                            color: record.isDirect ? '#722ed1' : '#1890ff'
                        }}
                        icon={<CarOutlined />}
                    />
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626', fontSize: 13 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record.phone}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            width: 100,
            render: (_, record) => {
                const cityName = record.city?.name || '-';
                return (
                    <Tag style={{ margin: 0, fontSize: 11 }}>{cityName}</Tag>
                );
            }
        },
        {
            title: 'Courses',
            key: 'rides',
            width: 80,
            align: 'center',
            render: (_, record) => {
                const currentRides = record.currentStats?.totalRides || record.totalRides || 0;
                return (
                    <div style={{
                        fontWeight: 700,
                        color: currentRides > 0 ? '#1890ff' : '#bfbfbf',
                        fontSize: 14
                    }}>
                        {currentRides}
                    </div>
                );
            }
        },
        {
            title: 'Progression',
            key: 'milestones',
            width: 280,
            render: (_, record) => {
                const m = record.milestones || {};
                const currentRides = record.currentStats?.totalRides || record.totalRides || 0;
                const isReactivation = record.driverType === 'reactivation';

                if (isReactivation) {
                    // Milestones réactivation
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {renderMilestoneTag(m.accountCreation?.achieved, 'Compte')}
                            {renderMilestoneTag(m.rides20Reactivation?.achieved, currentRides >= 20 ? '20' : `${Math.min(currentRides, 20)}/20`)}
                            {m.hours130Reactivation && renderMilestoneTag(m.hours130Reactivation?.achieved, '130h')}
                            <Tag color="orange" style={{ margin: '1px', padding: '0 6px', borderRadius: '10px', fontSize: 10 }}>
                                Réactiv.
                            </Tag>
                        </div>
                    );
                }

                // Milestones nouveau chauffeur - utiliser les seuils par chauffeur (basé sur sa ville)
                const t = record.milestoneThresholds || milestoneThresholds;
                const t1 = t.milestoneThreshold1 || 5;
                const t2 = t.milestoneThreshold2 || 15;
                const t3 = t.milestoneThreshold3 || 35;
                const t4 = t.milestoneThreshold4 || 55;
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {renderMilestoneTag(m.accountCreation?.achieved, 'Compte')}
                        {renderMilestoneTag(m.rides5?.achieved, currentRides >= t1 ? `${t1}` : `${currentRides}/${t1}`)}
                        {renderMilestoneTag(m.rides15?.achieved, currentRides >= t2 ? `${t2}` : `${Math.min(currentRides, t2)}/${t2}`)}
                        {renderMilestoneTag(m.rides35?.achieved, currentRides >= t3 ? `${t3}` : `${Math.min(currentRides, t3)}/${t3}`)}
                        {renderMilestoneTag(m.rides55?.achieved, currentRides >= t4 ? `${t4}` : `${Math.min(currentRides, t4)}/${t4}`)}
                    </div>
                );
            }
        },
        {
            title: 'Période',
            key: 'period',
            width: 150,
            render: (_, record) => {
                const startDate = record.trackingStartDate || record.recruitedAt;
                const endDate = record.activationWindowEnd;
                return (
                    <div style={{ fontSize: 11, color: '#595959' }}>
                        <div><CalendarOutlined style={{ marginRight: 4 }} />{startDate ? dayjs(startDate).format('DD/MM/YY') : '-'}</div>
                        <div style={{ color: '#8c8c8c' }}>→ {endDate ? dayjs(endDate).format('DD/MM/YY') : '-'}</div>
                    </div>
                );
            }
        },
        {
            title: 'Restant',
            key: 'remaining',
            width: 90,
            align: 'center',
            render: (_, record) => {
                const windowStatus = getWindowStatus(record);
                return (
                    <Tag
                        color={windowStatus.color}
                        icon={windowStatus.icon}
                        style={{ borderRadius: 10, fontSize: 11 }}
                    >
                        {windowStatus.text}
                    </Tag>
                );
            }
        },
        {
            title: 'Gains',
            key: 'revenue',
            width: 120,
            align: 'right',
            render: (_, record) => {
                const totalGains = record.totalRevenue || record.commissionsGenerated || 0;
                const paidAmount = record.paidAmount || 0;
                const remaining = Math.max(0, totalGains - paidAmount);
                const hasPartialPayment = paidAmount > 0 && paidAmount < totalGains;

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontWeight: 700, color: '#52c41a', fontSize: 13 }}>
                            {totalGains.toLocaleString()} F
                        </div>
                        {hasPartialPayment && (
                            <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                                <span style={{ color: '#52c41a' }}>Payé: {paidAmount.toLocaleString()}</span>
                                <span style={{ marginLeft: 4 }}>|</span>
                                <span style={{ color: '#faad14', marginLeft: 4 }}>Reste: {remaining.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Paiement',
            key: 'paymentStatus',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const status = record.paymentStatus || 'pending';
                const statusConfig = {
                    pending: { color: 'orange', text: 'Attente', icon: <ClockCircleOutlined /> },
                    paid: { color: 'green', text: 'Payé', icon: <CheckCircleOutlined /> },
                    partial: { color: 'blue', text: 'Partiel', icon: <WalletOutlined /> }
                };
                const config = statusConfig[status] || statusConfig.pending;
                return (
                    <Tag color={config.color} icon={config.icon} style={{ borderRadius: 10, fontSize: 11 }}>
                        {config.text}
                    </Tag>
                );
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size={4}>
                    <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setDetailModal({ visible: true, driver: record })}
                        style={{ color: '#1890ff' }}
                    />
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handlePaymentClick(record)}
                        style={{ color: '#52c41a' }}
                    />
                </Space>
            )
        }
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!beneficiary) {
        return (
            <div style={{ padding: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/revenues')} style={{ marginBottom: 16 }}>
                    Retour
                </Button>
                <Empty description="Bénéficiaire non trouvé" />
            </div>
        );
    }

    return (
        <div style={{ padding: '16px 24px', maxWidth: 1600, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20
            }}>
                <Space size="middle">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/revenues')}
                        style={{ borderRadius: 8 }}
                    />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#262626' }}>
                            Détails Revenus
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Inscrit le {dayjs(beneficiary.createdAt).format('DD MMM YYYY')}
                        </Text>
                    </div>
                </Space>
                <Space>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting} style={{ borderRadius: 8 }}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Popover content={baremeContent} title="Barème des commissions" trigger="click">
                        <Button icon={<InfoCircleOutlined />} style={{ borderRadius: 8 }}>
                            Barème
                        </Button>
                    </Popover>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchData}
                        style={{ borderRadius: 8 }}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Stats Cards - Horizontal */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                {/* Bénéficiaire */}
                <Col xs={24} sm={12} md={8} lg={6} xl={5}>
                    <Card
                        style={{ borderRadius: 12, height: '100%' }}
                        bodyStyle={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}
                    >
                        <Avatar
                            size={48}
                            style={{ background: themeColor, flexShrink: 0 }}
                            icon={isAffiliate ? <TeamOutlined /> : <CrownOutlined />}
                        />
                        <div style={{ minWidth: 0 }}>
                            <div style={{
                                fontWeight: 700,
                                fontSize: 15,
                                color: '#262626',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {beneficiary.firstName} {beneficiary.lastName}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Tag color={isAffiliate ? 'blue' : 'purple'} style={{ margin: 0, borderRadius: 8, fontSize: 11 }}>
                                    {isAffiliate ? 'Affilié' : 'Ambassadeur'}
                                </Tag>
                            </div>
                            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                <PhoneOutlined style={{ marginRight: 4 }} />
                                {beneficiary.phone || '-'}
                            </div>
                        </div>
                    </Card>
                </Col>

                {/* Chauffeurs */}
                <Col xs={12} sm={6} md={4} lg={3} xl={3}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CarOutlined style={{ fontSize: 22, color: '#1890ff', marginBottom: 4 }} />
                        <Statistic
                            value={totalDrivers}
                            valueStyle={{ fontSize: 24, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Chauffeurs</div>
                    </Card>
                </Col>

                {/* Complétés */}
                <Col xs={12} sm={6} md={4} lg={3} xl={3}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <TrophyOutlined style={{ fontSize: 22, color: '#52c41a', marginBottom: 4 }} />
                        <Statistic
                            value={completedDrivers}
                            valueStyle={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Complétés</div>
                    </Card>
                </Col>

                {/* Revenus Total */}
                <Col xs={12} sm={6} md={4} lg={4} xl={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <WalletOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={totalRevenue}
                            suffix="F"
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Revenus</div>
                    </Card>
                </Col>

                {/* Payé complet */}
                <Col xs={12} sm={6} md={4} lg={3} xl={3}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CheckCircleOutlined style={{ fontSize: 22, color: '#52c41a', marginBottom: 4 }} />
                        <Statistic
                            value={paidFullAmount}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Payé complet</div>
                        {paidFullDrivers.length > 0 && (
                            <div style={{ fontSize: 10, color: '#52c41a' }}>({paidFullDrivers.length} chauff.)</div>
                        )}
                    </Card>
                </Col>

                {/* Payé partiel */}
                <Col xs={12} sm={6} md={4} lg={3} xl={3}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <WalletOutlined style={{ fontSize: 22, color: '#1890ff', marginBottom: 4 }} />
                        <Statistic
                            value={paidPartialAmount}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#1890ff' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Payé partiel</div>
                        {paidPartialDrivers.length > 0 && (
                            <div style={{ fontSize: 10, color: '#1890ff' }}>({paidPartialDrivers.length} chauff.)</div>
                        )}
                    </Card>
                </Col>

                {/* En attente */}
                <Col xs={12} sm={6} md={4} lg={4} xl={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <ClockCircleOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={actualAmountToPay}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#faad14' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>À payer</div>
                        {remainingOnPartial > 0 && (
                            <div style={{ fontSize: 10, color: '#faad14' }}>(dont {remainingOnPartial.toLocaleString()} reste partiel)</div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Table - Full Width */}
            <Card
                style={{ borderRadius: 12 }}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>
                            Chauffeurs recrutés
                            <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{filterDrivers(drivers).length}</Tag>
                            {statusFilter && (
                                <Tag
                                    color={statusFilter === 'active' ? 'blue' : statusFilter === 'completed' ? 'green' : 'red'}
                                    style={{ marginLeft: 4, borderRadius: 12 }}
                                    closable
                                    onClose={() => setStatusFilter(null)}
                                >
                                    {statusFilter === 'active' ? 'Actifs' : statusFilter === 'completed' ? 'Complétés' : 'Expirés'}
                                </Tag>
                            )}
                            {cityFilter && (
                                <Tag
                                    color="cyan"
                                    style={{ marginLeft: 4, borderRadius: 12 }}
                                    closable
                                    onClose={() => setCityFilter(null)}
                                >
                                    {cityFilter}
                                </Tag>
                            )}
                            {dateRange && (
                                <Tag color="purple" style={{ marginLeft: 4, borderRadius: 12 }} closable onClose={() => setDateRange(null)}>
                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                    {dateRange[0]?.format('DD/MM')} - {dateRange[1]?.format('DD/MM')}
                                </Tag>
                            )}
                        </span>
                        <Space wrap>
                            <Select
                                placeholder="Statut"
                                value={statusFilter}
                                onChange={(value) => setStatusFilter(value)}
                                allowClear
                                size="small"
                                style={{ width: 130 }}
                                options={[
                                    { value: 'active', label: '🟢 Actifs' },
                                    { value: 'completed', label: '✅ Complétés' },
                                    { value: 'expired', label: '🔴 Expirés' }
                                ]}
                            />
                            {cityOptions.length >= 1 && (
                                <Select
                                    placeholder="Ville"
                                    value={cityFilter}
                                    onChange={(value) => setCityFilter(value)}
                                    allowClear
                                    size="small"
                                    style={{ width: 130 }}
                                    options={cityOptions}
                                />
                            )}
                            <RangePicker
                                placeholder={['Début', 'Fin']}
                                format="DD/MM/YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                                allowClear
                                size="small"
                                style={{ width: 220 }}
                                presets={[
                                    { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                    { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                    { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] }
                                ]}
                            />
                            <Input
                                placeholder="Rechercher..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 180, borderRadius: 8 }}
                                allowClear
                                size="small"
                            />
                        </Space>
                    </div>
                }
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={driverColumns}
                    dataSource={filterDrivers(drivers)}
                    rowKey="_id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total) => `${total} chauffeur${total > 1 ? 's' : ''}`,
                        size: 'small',
                        style: { marginRight: 16 }
                    }}
                    scroll={{ x: 1200 }}
                    locale={{ emptyText: <Empty description="Aucun chauffeur recruté" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    size="middle"
                />
            </Card>

            {/* Modal pour saisir la référence de paiement */}
            <Modal
                title={
                    <Space>
                        <DollarOutlined style={{ color: '#52c41a' }} />
                        <span>Enregistrer un paiement</span>
                    </Space>
                }
                open={paymentModal.visible}
                onOk={paymentMode === 'manual' ? handleSavePayment : undefined}
                onCancel={() => {
                    setPaymentModal({ visible: false, driver: null });
                    setPaymentRef('');
                    setPaymentStatusValue('pending');
                    setPaymentAmountValue(0);
                    setPaymentMode('wave');
                }}
                footer={paymentMode === 'wave' ? (
                    <Button onClick={() => {
                        setPaymentModal({ visible: false, driver: null });
                        setPaymentRef('');
                        setPaymentStatusValue('pending');
                        setPaymentAmountValue(0);
                        setPaymentMode('wave');
                    }}>Fermer</Button>
                ) : undefined}
                okText="Enregistrer"
                cancelText="Annuler"
                confirmLoading={savingPayment}
                okButtonProps={{ disabled: !paymentModal.driver?.progressId }}
                width={520}
                centered
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
            >
                {paymentModal.driver && (() => {
                    const currentGains = paymentModal.driver.totalRevenue || paymentModal.driver.commissionsGenerated || 0;
                    const alreadyPaid = paymentModal.driver.paidAmount || 0;
                    const remainingCurrent = Math.max(0, currentGains - alreadyPaid);
                    const maxPossible = getMaxPossibleGains(paymentModal.driver);
                    const remainingMax = Math.max(0, maxPossible - alreadyPaid);
                    const isReactivation = paymentModal.driver.driverType === 'reactivation';

                    return (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>
                                    {paymentModal.driver.firstName} {paymentModal.driver.lastName}
                                    <Tag color={isReactivation ? 'orange' : 'blue'} style={{ marginLeft: 8, fontSize: 11 }}>
                                        {isReactivation ? 'Réactivation' : 'Nouveau'}
                                    </Tag>
                                </div>

                                {/* Récapitulatif des montants */}
                                <div style={{
                                    background: '#fafafa',
                                    borderRadius: 8,
                                    padding: 12,
                                    marginBottom: 12
                                }}>
                                    <Row gutter={16}>
                                        <Col span={6}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: 10 }}>Gains actuels</div>
                                                <div style={{ fontWeight: 700, color: '#262626', fontSize: 15 }}>
                                                    {currentGains.toLocaleString()} F
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={6}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: 10 }}>Maximum</div>
                                                <div style={{ fontWeight: 700, color: '#722ed1', fontSize: 15 }}>
                                                    {maxPossible.toLocaleString()} F
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={6}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: 10 }}>Déjà payé</div>
                                                <div style={{ fontWeight: 700, color: '#52c41a', fontSize: 15 }}>
                                                    {alreadyPaid.toLocaleString()} F
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={6}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: 10 }}>Reste (max)</div>
                                                <div style={{ fontWeight: 700, color: remainingMax > 0 ? '#faad14' : '#52c41a', fontSize: 15 }}>
                                                    {remainingMax.toLocaleString()} F
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Info sur les gains actuels vs max */}
                                {currentGains < maxPossible && (
                                    <div style={{
                                        background: '#fff7e6',
                                        border: '1px solid #ffd591',
                                        borderRadius: 6,
                                        padding: '8px 12px',
                                        marginBottom: 12,
                                        fontSize: 12,
                                        color: '#ad6800'
                                    }}>
                                        <InfoCircleOutlined style={{ marginRight: 6 }} />
                                        Ce chauffeur peut encore gagner {(maxPossible - currentGains).toLocaleString()} F
                                        s'il atteint tous les objectifs.
                                    </div>
                                )}

                                {/* Historique des paiements si existant */}
                                {paymentModal.driver.paymentHistory && paymentModal.driver.paymentHistory.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ fontWeight: 500, marginBottom: 8, fontSize: 13 }}>
                                            Historique des paiements ({paymentModal.driver.paymentHistory.length})
                                        </div>
                                        <div style={{ maxHeight: 100, overflowY: 'auto', background: '#f5f5f5', borderRadius: 6, padding: 8 }}>
                                            {paymentModal.driver.paymentHistory.map((payment, index) => (
                                                <div key={index} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '4px 0',
                                                    borderBottom: index < paymentModal.driver.paymentHistory.length - 1 ? '1px solid #e8e8e8' : 'none',
                                                    fontSize: 12
                                                }}>
                                                    <span style={{ color: '#8c8c8c' }}>
                                                        {dayjs(payment.date).format('DD/MM/YY HH:mm')}
                                                    </span>
                                                    <span style={{ fontWeight: 600, color: '#52c41a' }}>
                                                        +{(payment.amount || 0).toLocaleString()} F
                                                    </span>
                                                    {payment.reference && (
                                                        <span style={{ color: '#8c8c8c', fontSize: 11 }}>
                                                            {payment.reference}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <Divider style={{ margin: '12px 0' }} />

                            {/* Sélecteur de mode de paiement */}
                            <div style={{ marginBottom: 20 }}>
                                <Segmented
                                    value={paymentMode}
                                    onChange={(value) => {
                                        setPaymentMode(value);
                                        if (value === 'wave') {
                                            setPaymentStatusValue('paid');
                                            setPaymentAmountValue(remainingCurrent);
                                        } else {
                                            setPaymentStatusValue('pending');
                                            setPaymentAmountValue(remainingCurrent);
                                        }
                                    }}
                                    block
                                    size="large"
                                    options={[
                                        {
                                            value: 'wave',
                                            label: (
                                                <div style={{ padding: '4px 0' }}>
                                                    <WalletOutlined style={{ marginRight: 6 }} />
                                                    Payer via Wave
                                                </div>
                                            )
                                        },
                                        {
                                            value: 'manual',
                                            label: (
                                                <div style={{ padding: '4px 0' }}>
                                                    <EditOutlined style={{ marginRight: 6 }} />
                                                    Saisie manuelle
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            </div>

                            {/* ===== MODE WAVE (Paiement automatique) ===== */}
                            {paymentMode === 'wave' && (
                                <div style={{
                                    background: '#f0f5ff',
                                    border: '1px solid #adc6ff',
                                    borderRadius: 10,
                                    padding: 20
                                }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ marginBottom: 6, fontWeight: 500 }}>Type de paiement</div>
                                        <Select
                                            value={paymentStatusValue === 'pending' ? 'paid' : paymentStatusValue}
                                            onChange={(value) => {
                                                setPaymentStatusValue(value);
                                                if (value === 'paid') {
                                                    setPaymentAmountValue(remainingCurrent);
                                                } else if (value === 'partial') {
                                                    setPaymentAmountValue(remainingCurrent);
                                                }
                                            }}
                                            style={{ width: '100%' }}
                                            size="large"
                                            options={[
                                                { value: 'partial', label: 'Paiement partiel' },
                                                { value: 'paid', label: `Paiement complet (${remainingCurrent.toLocaleString()} F)` }
                                            ]}
                                        />
                                    </div>

                                    {/* Champ montant pour paiement partiel Wave */}
                                    {paymentStatusValue === 'partial' && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 6, fontWeight: 500 }}>
                                                Montant à envoyer
                                                <span style={{ color: '#8c8c8c', fontWeight: 400, marginLeft: 8 }}>
                                                    (max: {remainingCurrent.toLocaleString()} F)
                                                </span>
                                            </div>
                                            <InputNumber
                                                value={paymentAmountValue}
                                                onChange={(value) => setPaymentAmountValue(value || 0)}
                                                style={{ width: '100%' }}
                                                size="large"
                                                min={1}
                                                max={remainingCurrent}
                                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                parser={(value) => value.replace(/\s/g, '')}
                                                addonAfter="F"
                                                placeholder="Montant"
                                            />
                                        </div>
                                    )}

                                    <Button
                                        type="primary"
                                        size="large"
                                        block
                                        icon={<WalletOutlined />}
                                        loading={sendingWavePayout}
                                        onClick={handleWavePayout}
                                        style={{
                                            background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)',
                                            border: 'none',
                                            height: 48,
                                            fontWeight: 600,
                                            borderRadius: 8
                                        }}
                                    >
                                        Envoyer {paymentStatusValue === 'partial' ? paymentAmountValue.toLocaleString() : remainingCurrent.toLocaleString()} F via Wave
                                    </Button>
                                    <div style={{ marginTop: 8, fontSize: 11, color: '#5b8c00', textAlign: 'center' }}>
                                        Le paiement sera envoyé directement sur Wave et la référence sera automatiquement enregistrée
                                    </div>
                                </div>
                            )}

                            {/* ===== MODE MANUEL (Saisie manuelle) ===== */}
                            {paymentMode === 'manual' && (
                                <div style={{
                                    background: '#fffbe6',
                                    border: '1px solid #ffe58f',
                                    borderRadius: 10,
                                    padding: 20
                                }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ marginBottom: 6, fontWeight: 500 }}>Type de paiement</div>
                                        <Select
                                            value={paymentStatusValue}
                                            onChange={(value) => {
                                                setPaymentStatusValue(value);
                                                if (value === 'paid') {
                                                    setPaymentAmountValue(remainingCurrent);
                                                } else if (value === 'partial') {
                                                    setPaymentAmountValue(remainingCurrent);
                                                }
                                            }}
                                            style={{ width: '100%' }}
                                            size="large"
                                            options={[
                                                { value: 'pending', label: 'Remettre en attente' },
                                                { value: 'partial', label: 'Paiement partiel' },
                                                { value: 'paid', label: `Paiement complet (${remainingCurrent.toLocaleString()} F)` }
                                            ]}
                                        />
                                        <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                                            {paymentStatusValue === 'partial' && (
                                                <>Le statut sera "Partiel" car le chauffeur peut encore atteindre {maxPossible.toLocaleString()} F</>
                                            )}
                                            {paymentStatusValue === 'paid' && remainingCurrent < remainingMax && (
                                                <>Paye les gains actuels ({remainingCurrent.toLocaleString()} F). Statut = Partiel car max = {maxPossible.toLocaleString()} F</>
                                            )}
                                            {paymentStatusValue === 'paid' && remainingCurrent >= remainingMax && (
                                                <>Tout sera payé. Statut = Payé</>
                                            )}
                                        </div>
                                    </div>

                                    {/* Champ montant pour paiement partiel manuel */}
                                    {paymentStatusValue === 'partial' && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 6, fontWeight: 500 }}>
                                                Montant payé
                                                <span style={{ color: '#8c8c8c', fontWeight: 400, marginLeft: 8 }}>
                                                    (gains actuels dispo: {remainingCurrent.toLocaleString()} F)
                                                </span>
                                            </div>
                                            <InputNumber
                                                value={paymentAmountValue}
                                                onChange={(value) => setPaymentAmountValue(value || 0)}
                                                style={{ width: '100%' }}
                                                size="large"
                                                min={0}
                                                max={remainingCurrent}
                                                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                parser={(value) => value.replace(/\s/g, '')}
                                                addonAfter="F"
                                                placeholder="Montant"
                                            />
                                            {paymentAmountValue > 0 && (
                                                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                                                    Après ce paiement: <span style={{ fontWeight: 600, color: '#52c41a' }}>
                                                        {(alreadyPaid + paymentAmountValue).toLocaleString()} F payé
                                                    </span>
                                                    {' / '}
                                                    <span style={{ fontWeight: 600, color: '#faad14' }}>
                                                        {Math.max(0, maxPossible - alreadyPaid - paymentAmountValue).toLocaleString()} F reste (sur {maxPossible.toLocaleString()})
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div>
                                        <div style={{ marginBottom: 6, fontWeight: 500 }}>Référence de paiement</div>
                                        <Input
                                            placeholder="Ex: TX-2025021001234"
                                            value={paymentRef}
                                            onChange={(e) => setPaymentRef(e.target.value)}
                                            size="large"
                                            prefix={<WalletOutlined style={{ color: '#bfbfbf' }} />}
                                        />
                                        <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
                                            Entrez la référence si vous avez déjà effectué le paiement
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </Modal>

            {/* Modal détail du chauffeur */}
            <Modal
                title={
                    <Space>
                        <CarOutlined style={{ color: themeColor }} />
                        <span>Détails du chauffeur</span>
                    </Space>
                }
                open={detailModal.visible}
                onCancel={() => setDetailModal({ visible: false, driver: null })}
                footer={[
                    <Button key="close" onClick={() => setDetailModal({ visible: false, driver: null })}>
                        Fermer
                    </Button>,
                    <Button
                        key="payment"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setDetailModal({ visible: false, driver: null });
                            handlePaymentClick(detailModal.driver);
                        }}
                    >
                        Modifier paiement
                    </Button>
                ]}
                width={600}
                centered
                styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
            >
                {detailModal.driver && (() => {
                    const d = detailModal.driver;
                    const windowStatus = getWindowStatus(d);
                    const currentRides = d.currentStats?.totalRides || d.totalRides || 0;
                    const m = d.milestones || {};

                    return (
                        <div>
                            {/* Info chauffeur */}
                            <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Avatar size={48} style={{ background: themeColor }} icon={<CarOutlined />} />
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 16 }}>
                                            {d.firstName} {d.lastName}
                                        </div>
                                        <div style={{ color: '#8c8c8c' }}>
                                            <PhoneOutlined style={{ marginRight: 4 }} />{d.phone}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* Période d'activation */}
                            <Card
                                size="small"
                                title={<span><CalendarOutlined style={{ marginRight: 8 }} />Période d'activation</span>}
                                style={{ marginBottom: 16 }}
                            >
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>Début</div>
                                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                {(d.trackingStartDate || d.recruitedAt) ? dayjs(d.trackingStartDate || d.recruitedAt).format('DD/MM/YYYY') : '-'}
                                            </div>
                                            <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                                                {(d.trackingStartDate || d.recruitedAt) ? dayjs(d.trackingStartDate || d.recruitedAt).format('HH:mm') : ''}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>Fin</div>
                                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                {d.activationWindowEnd ? dayjs(d.activationWindowEnd).format('DD/MM/YYYY') : '-'}
                                            </div>
                                            <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                                                {d.activationWindowEnd ? dayjs(d.activationWindowEnd).format('HH:mm') : ''}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ color: '#8c8c8c', fontSize: 12 }}>Statut</div>
                                            <Tag
                                                color={windowStatus.color}
                                                icon={windowStatus.icon}
                                                style={{ margin: '4px 0', borderRadius: 10 }}
                                            >
                                                {windowStatus.text}
                                            </Tag>
                                            <div style={{ color: '#8c8c8c', fontSize: 11 }}>
                                                {d.driverType === 'reactivation' ? 'Réactivation' : 'Nouveau'}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Progression */}
                            {(() => {
                                const driverMilestones = getMilestonesForDriver(isAffiliate, d.driverType, d.milestoneThresholds || milestoneThresholds, d.commissionRates || commissionRates);
                                return (
                                    <Card
                                        size="small"
                                        title={<span><TrophyOutlined style={{ marginRight: 8 }} />Progression ({currentRides} courses)</span>}
                                        style={{ marginBottom: 16 }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {driverMilestones.map((milestone, index) => {
                                                const achieved = milestone.milestoneKey && m[milestone.milestoneKey]?.achieved;
                                                const achievedAt = milestone.milestoneKey && m[milestone.milestoneKey]?.achievedAt;

                                                return (
                                                    <div
                                                        key={milestone.key}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '8px 12px',
                                                            background: achieved ? '#f6ffed' : '#fafafa',
                                                            borderRadius: 8,
                                                            border: achieved ? '1px solid #b7eb8f' : '1px solid #f0f0f0'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            {achieved ? (
                                                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                            ) : (
                                                                <ClockCircleOutlined style={{ color: '#bfbfbf' }} />
                                                            )}
                                                            <span style={{ fontWeight: achieved ? 600 : 400, color: achieved ? '#262626' : '#8c8c8c' }}>
                                                                {milestone.label}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            {achievedAt && (
                                                                <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                                    {dayjs(achievedAt).format('DD/MM HH:mm')}
                                                                </span>
                                                            )}
                                                            <span style={{ fontWeight: 600, color: achieved ? '#52c41a' : '#bfbfbf' }}>
                                                                +{milestone.amount} F
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Card>
                                );
                            })()}

                            {/* Paiement */}
                            {(() => {
                                const totalGains = d.totalRevenue || d.commissionsGenerated || 0;
                                const alreadyPaid = d.paidAmount || 0;
                                const remaining = Math.max(0, totalGains - alreadyPaid);

                                return (
                                    <Card
                                        size="small"
                                        title={<span><WalletOutlined style={{ marginRight: 8 }} />Paiement</span>}
                                    >
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>Gains totaux</div>
                                                    <div style={{ fontWeight: 700, fontSize: 16, color: '#262626' }}>
                                                        {totalGains.toLocaleString()} F
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={6}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>Déjà payé</div>
                                                    <div style={{ fontWeight: 700, fontSize: 16, color: '#52c41a' }}>
                                                        {alreadyPaid.toLocaleString()} F
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={6}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>Reste à payer</div>
                                                    <div style={{ fontWeight: 700, fontSize: 16, color: remaining > 0 ? '#faad14' : '#52c41a' }}>
                                                        {remaining.toLocaleString()} F
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={6}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ color: '#8c8c8c', fontSize: 11 }}>Statut</div>
                                                    <Tag
                                                        color={d.paymentStatus === 'paid' ? 'green' : d.paymentStatus === 'partial' ? 'blue' : 'orange'}
                                                        style={{ margin: '4px 0', borderRadius: 10 }}
                                                    >
                                                        {d.paymentStatus === 'paid' ? 'Payé' : d.paymentStatus === 'partial' ? 'Partiel' : 'En attente'}
                                                    </Tag>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Historique des paiements partiels */}
                                        {d.paymentHistory && d.paymentHistory.length > 0 && (
                                            <div style={{ marginTop: 16 }}>
                                                <div style={{ fontWeight: 500, marginBottom: 8, fontSize: 12, color: '#595959' }}>
                                                    Historique des paiements ({d.paymentHistory.length})
                                                </div>
                                                <div style={{ background: '#fafafa', borderRadius: 6, padding: 8 }}>
                                                    {d.paymentHistory.map((payment, index) => (
                                                        <div key={index} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            borderBottom: index < d.paymentHistory.length - 1 ? '1px solid #f0f0f0' : 'none'
                                                        }}>
                                                            <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                                                                {dayjs(payment.date).format('DD/MM/YYYY HH:mm')}
                                                            </span>
                                                            <span style={{ fontWeight: 600, color: '#52c41a', fontSize: 13 }}>
                                                                +{(payment.amount || 0).toLocaleString()} F
                                                            </span>
                                                            <span style={{ color: '#8c8c8c', fontSize: 11 }}>
                                                                {payment.reference || '-'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Dernière référence si pas d'historique */}
                                        {(!d.paymentHistory || d.paymentHistory.length === 0) && d.paymentReference && (
                                            <div style={{ marginTop: 12, textAlign: 'center' }}>
                                                <div style={{ color: '#8c8c8c', fontSize: 11 }}>Référence</div>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>{d.paymentReference}</div>
                                            </div>
                                        )}
                                    </Card>
                                );
                            })()}
                        </div>
                    );
                })()}
            </Modal>
        </div>
    );
};

export default RevenuesDetailPage;
